import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PenTool, Plus, Trash2, Loader2, ChevronDown } from 'lucide-react';

/**
 * Smallpdf-style Sign dropdown — anchored to the Sign toolbar button, shows
 * saved signatures inline with delete + "+ New signature". Rendered as a
 * portal-less popover that positions itself relative to the trigger button.
 *
 * Only rendered when the user is signed in. Guests see the plain Sign button
 * that opens the Create modal directly — that branch lives in EditToolbar.
 *
 * Behaviour:
 *   - Click a saved row → onSelect(dataUrl) (closes popover, enters place mode upstream).
 *   - Click the trash → onDelete(id) with a native confirm.
 *   - Click "+ New signature" → onAddNew() (opens Create modal upstream). Disabled at plan cap.
 *   - Zero signatures → popover shows just "+ New signature".
 *   - Click outside / Esc → closes.
 *
 * Keyboard-focus: first actionable element receives focus on open; Esc closes.
 */
export default function SignatureMenu({
  active,
  signatures,
  loading,
  plan,
  cap,
  used,
  capReached,
  onSelect,
  onDelete,
  onAddNew,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  // Simple focus handoff — first actionable element after open.
  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    const focusable = popoverRef.current.querySelector('button, [href]');
    focusable?.focus();
  }, [open]);

  const handleSelect = (sig) => {
    setOpen(false);
    onSelect(sig);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (typeof window !== 'undefined' && !window.confirm('Delete this signature?')) return;
    onDelete(id);
  };

  const handleAddNew = () => {
    setOpen(false);
    onAddNew();
  };

  const newTooltip = capReached
    ? (plan === 'pro'
      ? `You've reached the ${cap}-signature limit. Delete one to add another.`
      : 'Upgrade to Pro to save more signatures.')
    : 'Create a new signature';

  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors ${
          active || open ? 'bg-brand-blue text-white' : 'text-gray-600 hover:bg-gray-100'
        }`}
        title="Sign"
      >
        <PenTool size={15} />
        <span className="text-xs hidden sm:inline">Sign</span>
        <ChevronDown
          size={10}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${active || open ? 'text-white/80' : 'text-gray-400'}`}
        />
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="menu"
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 w-72"
        >
          <div className="px-3 pb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Signatures</span>
            <span className="text-[10px] text-gray-400">{used} of {cap}</span>
          </div>

          {loading ? (
            <div className="px-3 py-4 flex items-center text-gray-500 text-sm">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              {signatures.length > 0 && (
                <div className="max-h-56 overflow-y-auto">
                  {signatures.map((sig) => (
                    <div
                      key={sig.id}
                      className="group flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => handleSelect(sig)}
                        className="flex-1 flex items-center gap-3 text-left"
                        title="Use this signature"
                      >
                        <div className="w-24 h-10 flex items-center justify-center bg-white border border-gray-100 rounded shrink-0">
                          <img
                            src={sig.dataUrl}
                            alt={sig.label || 'Signature'}
                            className="max-h-8 max-w-full object-contain"
                          />
                        </div>
                        {sig.label && (
                          <span className="text-xs text-gray-600 truncate">{sig.label}</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDelete(e, sig.id)}
                        className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete signature"
                        aria-label="Delete signature"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Future "Initials" slot would go here. */}

              <div className={signatures.length > 0 ? 'border-t border-gray-100 mt-1 pt-1' : ''}>
                <button
                  type="button"
                  onClick={handleAddNew}
                  disabled={capReached}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-blue hover:bg-brand-light/10 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                  title={newTooltip}
                >
                  <Plus size={14} />
                  <span>New signature</span>
                </button>
                {capReached && (
                  <p className="px-3 pb-2 text-[11px] text-gray-500 leading-snug">
                    {plan === 'pro'
                      ? 'Delete one to add another.'
                      : (
                        <>
                          Upgrade to Pro for up to 3 signatures.{' '}
                          <a
                            href="https://scrivar.com/#pricing"
                            target="_blank"
                            rel="noreferrer"
                            className="text-brand-blue hover:underline"
                          >
                            See plans
                          </a>
                        </>
                      )
                    }
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
